import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertTriangle } from 'lucide-react'
import { BULK_TARGET_STATUSES, statusDisplayLabel } from './bulk-status-update-bar.helpers'

interface SelectorProps {
  newStatus: string
  onChange: (value: string) => void
  t: TFunction<'cases'>
}

export function StatusSelect({ newStatus, onChange, t }: SelectorProps): ReactElement {
  return (
    <select
      value={newStatus}
      onChange={(e) => {
        onChange(e.target.value)
      }}
      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
    >
      <option value="">{t('collections.targetMonitoring.bulkUpdateTitle')}</option>
      {BULK_TARGET_STATUSES.map((status) => (
        <option key={status} value={status}>
          {statusDisplayLabel(status)}
        </option>
      ))}
    </select>
  )
}

interface HaltedNoteFieldProps {
  note: string
  onChange: (value: string) => void
  t: TFunction<'cases'>
}

export function HaltedNoteField({ note, onChange, t }: HaltedNoteFieldProps): ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
        {t('collections.haltedNoteLabel')}
      </span>
      <textarea
        value={note}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('collections.haltedNotePlaceholder')}
        rows={1}
        className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      />
    </label>
  )
}

interface ConfirmProps {
  selectedCount: number
  statusLabel: string
  updating: boolean | undefined
  disabled: boolean
  onConfirm: () => void
  onCancel: () => void
  t: TFunction<'cases'>
}

export function ConfirmActions({
  selectedCount,
  statusLabel,
  updating,
  disabled,
  onConfirm,
  onCancel,
  t,
}: ConfirmProps): ReactElement {
  const isUpdating = updating ?? false
  return (
    <div className="items-centre flex gap-2">
      <span className="items-centre flex gap-1 text-xs text-orange-600 dark:text-orange-400">
        <AlertTriangle className="h-3.5 w-3.5" />
        {t('collections.targetMonitoring.bulkUpdateMessage', {
          count: selectedCount,
          status: statusLabel,
        })}
      </span>
      <button
        onClick={onConfirm}
        disabled={isUpdating || disabled}
        className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:ring-2 focus:ring-[var(--accent)] focus:outline-none disabled:opacity-50"
      >
        {t('collections.targetMonitoring.bulkUpdateConfirm')}
      </button>
      <button
        onClick={onCancel}
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('collections.artefactTemplates.cancel')}
      </button>
    </div>
  )
}

interface PrimaryProps {
  newStatus: string
  updating: boolean | undefined
  disabled: boolean
  onClick: () => void
  t: TFunction<'cases'>
}

export function StartConfirmButton({
  newStatus,
  updating,
  disabled,
  onClick,
  t,
}: PrimaryProps): ReactElement {
  const isUpdating = updating ?? false
  return (
    <button
      onClick={onClick}
      disabled={!newStatus || isUpdating || disabled}
      className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:ring-2 focus:ring-[var(--accent)] focus:outline-none disabled:opacity-50"
    >
      {t('collections.targetMonitoring.bulkUpdateConfirm')}
    </button>
  )
}
